import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useAdminBanUser,
  useAdminCreateUser,
  useAdminRemoveUser,
  useAdminSetPassword,
  useAdminSetRole,
  useAdminUnbanUser,
  useAdminUsersQuery,
} from "@/hooks/useAdminUsers";
import { useMailboxesQuery } from "@/hooks/useMailboxes";
import { useAuth } from "@/auth/AuthContext";

type AdminUser = {
  id: string;
  email: string;
  name: string;
  role?: string | null;
  banned?: boolean | null;
};

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const usersQuery = useAdminUsersQuery();
  const mailboxesQuery = useMailboxesQuery();

  const createUser = useAdminCreateUser();
  const setRole = useAdminSetRole();
  const setPassword = useAdminSetPassword();
  const banUser = useAdminBanUser();
  const unbanUser = useAdminUnbanUser();
  const removeUser = useAdminRemoveUser();

  // Create dialog state
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState<"user" | "admin">("user");

  // Password reset dialog state
  const [resetUser, setResetUser] = useState<AdminUser | null>(null);
  const [newPwd, setNewPwd] = useState("");

  const users = (usersQuery.data?.users ?? []) as AdminUser[];
  const mailboxes = mailboxesQuery.data?.items ?? [];

  function getMailboxesForUser(userId: string) {
    return mailboxes.filter((m) => m.users.some((u) => u.id === userId));
  }

  function handleCreate() {
    createUser.mutate(
      { email: newEmail, password: newPassword, name: newName, role: newRole },
      {
        onSuccess: () => {
          setShowCreate(false);
          setNewEmail("");
          setNewPassword("");
          setNewName("");
          setNewRole("user");
        },
      },
    );
  }

  function handleResetPassword() {
    if (!resetUser) return;
    setPassword.mutate(
      { userId: resetUser.id, newPassword: newPwd },
      {
        onSuccess: () => {
          setResetUser(null);
          setNewPwd("");
        },
      },
    );
  }

  function handleRemove(user: AdminUser) {
    if (!confirm(`Rimuovere l'utente ${user.email}? L'azione non è reversibile.`)) return;
    removeUser.mutate(user.id);
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Utenti</h1>
          <p className="text-sm text-muted-foreground">
            Gestione degli account e dei ruoli. Per assegnare mailbox usa la pagina Mailbox.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>Crea utente</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista utenti</CardTitle>
          <CardDescription>
            {usersQuery.isLoading ? "Caricamento..." : `${users.length} utenti`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {usersQuery.isError ? (
            <p className="text-sm text-destructive">
              Errore nel caricamento degli utenti.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Ruolo</TableHead>
                  <TableHead>Mailbox</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => {
                  const userMailboxes = getMailboxesForUser(u.id);
                  return (
                    <TableRow key={u.id}>
                      <TableCell className="font-mono text-sm">{u.email}</TableCell>
                      <TableCell>{u.name}</TableCell>
                      <TableCell>
                        <select
                          value={u.role ?? "user"}
                          onChange={(e) =>
                            setRole.mutate({
                              userId: u.id,
                              role: e.target.value as "user" | "admin",
                            })
                          }
                          className="rounded border bg-background px-2 py-1 text-sm"
                        >
                          <option value="user">user</option>
                          <option value="admin">admin</option>
                        </select>
                      </TableCell>
                      <TableCell>
                        {userMailboxes.length === 0 ? (
                          <span className="text-xs text-muted-foreground">nessuna</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {userMailboxes.map((m) => (
                              <Badge key={m.id} variant="outline">{m.label}</Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.banned ? (
                          <Badge variant="red">Bannato</Badge>
                        ) : (
                          <Badge variant="secondary">Attivo</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setResetUser(u); setNewPwd(""); }}
                          >
                            Reset pwd
                          </Button>
                          {u.id !== currentUser?.id && (
                            <>
                              {u.banned ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => unbanUser.mutate(u.id)}
                                >
                                  Riabilita
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => banUser.mutate({ userId: u.id })}
                                >
                                  Banna
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleRemove(u)}
                              >
                                Rimuovi
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create user dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crea utente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-email">Email</Label>
              <Input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="utente@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-name">Nome</Label>
              <Input
                id="new-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Nome Cognome"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">Password iniziale</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-role">Ruolo</Label>
              <select
                id="new-role"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as "user" | "admin")}
                className="w-full rounded border bg-background px-3 py-2 text-sm"
              >
                <option value="user">user</option>
                <option value="admin">admin</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleCreate}
              disabled={createUser.isPending || !newEmail || !newPassword || !newName}
            >
              {createUser.isPending ? "Creazione..." : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog
        open={resetUser !== null}
        onOpenChange={(open) => { if (!open) setResetUser(null); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password — {resetUser?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reset-pwd">Nuova password</Label>
            <Input
              id="reset-pwd"
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUser(null)}>
              Annulla
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={setPassword.isPending || newPwd.length < 8}
            >
              {setPassword.isPending ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
